/**
 * The multi-value picker of the prototype's View builder (views.js `setPicker`: `.vb-picker` details
 * with a search box and checkboxes), reused for set conditions in rules (source, type, tag, topic).
 */

import { useState } from 'react';
import { Icon } from '../../icons/Icon.js';

export interface PickerOption {
  id: string;
  name: string;
  icon: string;
}

export interface MultiPickerProps {
  /** Noun for the placeholder and labels: "sources", "tags" … */
  noun: string;
  options: readonly PickerOption[];
  values: readonly string[];
  onChange(values: string[]): void;
}

export function MultiPicker({ noun, options, values, onChange }: MultiPickerProps) {
  const [search, setSearch] = useState('');
  const all = [...options];
  for (const v of values) if (!all.some((o) => o.id === v)) all.push({ id: v, name: v, icon: 'circle-help' });
  const q = search.trim().toLowerCase();
  const shown = all.filter((o) => o.name.toLowerCase().includes(q));
  const selected = values.map((v) => all.find((o) => o.id === v)).filter((o): o is PickerOption => o !== undefined);
  const toggle = (id: string, on: boolean): void => onChange(on ? [...values, id] : values.filter((v) => v !== id));
  return (
    <details className="vb-picker rule-picker">
      <summary aria-label={`Choose ${noun}`}>
        {selected.length === 0 ? (
          <span className="vb-placeholder">Choose {noun}…</span>
        ) : (
          <span className="vb-selection-items">
            {selected.slice(0, 2).map((o) => (
              <span key={o.id}>
                <Icon name={o.icon} />
                {o.name}
              </span>
            ))}
            {selected.length > 2 ? <b>+{selected.length - 2}</b> : null}
          </span>
        )}
        <Icon name="chevron-down" />
      </summary>
      <div className="vb-picker-menu">
        <label className="vb-picker-search">
          <Icon name="search" />
          <input type="search" value={search} aria-label={`Search ${noun}`} placeholder="Search…" onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="vb-picker-options">
          {shown.map((o) => (
            <label key={o.id}>
              <input type="checkbox" value={o.id} checked={values.includes(o.id)} onChange={(e) => toggle(o.id, e.target.checked)} />
              <span>
                <Icon name={o.icon} />
              </span>
              <span>{o.name}</span>
            </label>
          ))}
        </div>
        {shown.length === 0 ? <p className="vb-picker-empty">No matching items</p> : null}
      </div>
    </details>
  );
}
